import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, Loader2, Info, CalendarCheck, ImagePlus, X } from 'lucide-react';
import Navbar from '../../components/Navbar';
import { VegIndicator } from '../../components/VegIndicator';
import { Switch } from '../../components/ui/switch';
import { PreorderPhoto } from '../../components/PreorderPhoto';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PERIODS = ['breakfast', 'lunch', 'snacks', 'dinner'];
const EMPTY = { name: '', description: '', price: '', is_veg: true, meal_periods: [], is_active: true, image_url: '' };

const PhotoField = ({ url, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setErr('Image must be under 5 MB'); return; }
    setUploading(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/vendor/preorder-items/upload-image`, fd, { withCredentials: true });
      onChange(data.url);
    } catch (ex) { setErr(ex.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };
  return (
    <div className="flex items-center gap-3 mt-4">
      <PreorderPhoto url={url} alt="Item photo" size="h-16 w-16" testId="preorder-form-photo" />
      <label data-testid="preorder-form-photo-upload"
        className="cursor-pointer px-3 py-1.5 rounded-lg border border-border-light text-xs font-medium flex items-center gap-1.5 hover:border-primary">
        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
        {url ? 'Change photo' : 'Add photo (optional)'}
        <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={pick} disabled={uploading}
          data-testid="preorder-form-photo-input" />
      </label>
      {url && (
        <button type="button" onClick={() => onChange('')} data-testid="preorder-form-photo-remove"
          className="text-xs text-red-600 flex items-center gap-1 hover:underline"><X className="h-3.5 w-3.5" /> Remove</button>
      )}
      {err && <span data-testid="preorder-form-photo-error" className="text-xs text-red-600">{err}</span>}
    </div>
  );
};

const ItemForm = ({ initial, onCancel, onSave, saving }) => {
  const [f, setF] = useState(initial);
  const togglePeriod = (p) => setF((s) => ({
    ...s, meal_periods: s.meal_periods.includes(p) ? s.meal_periods.filter((x) => x !== p) : [...s.meal_periods, p],
  }));
  const input = 'w-full px-3 py-2 border border-border-light rounded-lg bg-background text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary';
  return (
    <div data-testid="preorder-item-form" className="bg-card border border-primary/30 rounded-2xl p-5 mb-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2">
          <label className="text-xs text-text-muted">Item name</label>
          <input data-testid="preorder-form-name" className={input} value={f.name} maxLength={80}
            onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Paneer Butter Masala Thali" />
        </div>
        <div>
          <label className="text-xs text-text-muted">Price (₹)</label>
          <input data-testid="preorder-form-price" type="number" min="1" className={input} value={f.price}
            onChange={(e) => setF({ ...f, price: e.target.value })} />
        </div>
        <div className="sm:col-span-3">
          <label className="text-xs text-text-muted">Description (optional)</label>
          <input data-testid="preorder-form-description" className={input} value={f.description} maxLength={240}
            onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="2 rotis, rice, dal, salad" />
        </div>
      </div>
      <PhotoField url={f.image_url} onChange={(u) => setF({ ...f, image_url: u })} />
      <div className="flex flex-wrap items-center gap-2 mt-4">
        <span className="text-xs text-text-muted mr-1">Type:</span>
        {[true, false].map((veg) => (
          <button key={String(veg)} type="button" onClick={() => setF({ ...f, is_veg: veg })}
            data-testid={`preorder-form-${veg ? 'veg' : 'nonveg'}`}
            className={`px-3 py-1.5 rounded-full border text-xs font-medium flex items-center gap-1.5 transition-colors ${
              f.is_veg === veg ? 'border-primary bg-primary-light text-primary' : 'border-border-light text-text-secondary'}`}>
            <VegIndicator isVeg={veg} size="sm" /> {veg ? 'Veg' : 'Non-Veg'}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <span className="text-xs text-text-muted mr-1">Available for:</span>
        {PERIODS.map((p) => (
          <button key={p} type="button" onClick={() => togglePeriod(p)} data-testid={`preorder-form-period-${p}`}
            className={`px-3 py-1.5 rounded-full border text-xs capitalize transition-colors ${
              f.meal_periods.includes(p) ? 'border-primary bg-primary text-white' : 'border-border-light text-text-secondary'}`}>
            {p}
          </button>
        ))}
        <span className="text-[11px] text-text-muted">{f.meal_periods.length ? '' : '(none picked = all meals)'}</span>
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onCancel} data-testid="preorder-form-cancel" className="px-4 py-2 text-sm rounded-lg border border-border-light">Cancel</button>
        <button onClick={() => onSave(f)} disabled={saving || !f.name.trim() || !(Number(f.price) > 0)}
          data-testid="preorder-form-save"
          className="px-5 py-2 text-sm rounded-lg bg-primary hover:bg-primary-hover text-white disabled:opacity-40">
          {saving ? 'Saving...' : 'Save item'}
        </button>
      </div>
    </div>
  );
};

const ItemRow = ({ it, onEdit, onToggle, onDelete }) => (
  <li data-testid={`preorder-row-${it.id}`} className="flex flex-wrap items-center gap-3 px-4 py-3 border-t border-border-light first:border-t-0">
    <div className="flex items-start gap-2.5 min-w-0 flex-1 basis-48">
      <PreorderPhoto url={it.image_url} alt={it.name} size="h-12 w-12" testId={`preorder-row-photo-${it.id}`} />
      <VegIndicator isVeg={it.is_veg} size="sm" className="mt-1" />
      <div className="min-w-0">
        <p className="text-sm font-medium text-text-primary break-words">{it.name}</p>
        {it.description && <p className="text-xs text-text-muted break-words">{it.description}</p>}
        <p className="text-xs text-text-secondary mt-0.5 capitalize">
          <span data-testid={`preorder-row-price-${it.id}`} className="font-semibold text-text-primary">₹{it.price.toFixed(0)}</span>
          {' · '}{it.meal_periods.length ? it.meal_periods.join(', ') : 'All meals'}
        </p>
      </div>
    </div>
    <div className="flex items-center gap-2 ml-auto">
      <Switch checked={it.is_active} onCheckedChange={() => onToggle(it)} data-testid={`preorder-row-active-${it.id}`}
        className="data-[state=unchecked]:bg-gray-300" />
      <span className={`text-xs w-11 ${it.is_active ? 'text-emerald-700' : 'text-text-muted'}`}>{it.is_active ? 'Active' : 'Hidden'}</span>
      <button onClick={() => onEdit(it)} data-testid={`preorder-row-edit-${it.id}`} className="p-2 rounded-lg text-primary hover:bg-primary-light"><Pencil className="h-4 w-4" /></button>
      <button onClick={() => onDelete(it)} data-testid={`preorder-row-delete-${it.id}`} className="p-2 rounded-lg text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
    </div>
  </li>
);

export default function VendorPreorderMenu() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/vendor/preorder-items`, { withCredentials: true });
      setData(data);
    } catch (e) { setError(e.response?.data?.detail || 'Could not load pre-order menu'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (f) => {
    setSaving(true); setError('');
    const body = { ...f, price: Number(f.price) };
    delete body.id; delete body.vendor_id;
    try {
      if (f.id) await axios.patch(`${API}/vendor/preorder-items/${f.id}`, body, { withCredentials: true });
      else await axios.post(`${API}/vendor/preorder-items`, body, { withCredentials: true });
      setForm(null); await load();
    } catch (e) { setError(e.response?.data?.detail?.toString() || 'Save failed'); }
    finally { setSaving(false); }
  };
  const toggle = async (it) => {
    await axios.patch(`${API}/vendor/preorder-items/${it.id}`, { is_active: !it.is_active }, { withCredentials: true });
    load();
  };
  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.name}"?`)) return;
    await axios.delete(`${API}/vendor/preorder-items/${it.id}`, { withCredentials: true });
    load();
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-background">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
            <div className="min-w-0">
              <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold text-text-primary">Pre-order Menu</h1>
              <p className="text-sm text-text-secondary mt-1">Items employees can pre-order (and pay for) for tomorrow.</p>
            </div>
            <div className="flex gap-2">
              <Link to="/vendor/reservations" data-testid="preorder-view-reservations" className="px-4 py-2.5 rounded-lg border border-border-light text-sm flex items-center gap-1.5 hover:border-primary"><CalendarCheck className="h-4 w-4" /> Reservations</Link>
              <button onClick={() => setForm({ ...EMPTY })} data-testid="preorder-add-btn"
                className="px-4 py-2.5 rounded-lg bg-primary hover:bg-primary-hover text-white text-sm font-medium flex items-center gap-1.5">
                <Plus className="h-4 w-4" /> Add item
              </button>
            </div>
          </div>

          {data && (
            <div data-testid="preorder-mode-banner" className={`rounded-2xl p-4 mb-6 flex items-start gap-3 text-sm border ${
              data.mode === 'custom' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
              <Info className="h-5 w-5 flex-shrink-0 mt-0.5" />
              {data.mode === 'custom'
                ? <span><strong>Live:</strong> employees see these active items when they pre-order from you.</span>
                : <span><strong>Not live yet:</strong> your admin has set standard meals (Veg / Non-Veg) for your outlet. These items will show to employees once the admin switches you to "Vendor's own menu".</span>}
            </div>
          )}
          {error && <div data-testid="preorder-error" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
          {form && <ItemForm key={form.id || 'new'} initial={form} saving={saving} onCancel={() => setForm(null)} onSave={save} />}

          {!data ? (
            <div className="py-16 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
          ) : data.items.length === 0 ? (
            <p data-testid="preorder-empty" className="bg-card border border-border-light rounded-2xl p-10 text-center text-text-muted">No pre-order items yet. Add your first one.</p>
          ) : (
            <ul data-testid="preorder-list" className="bg-card border border-border-light rounded-2xl">
              {data.items.map((it) => (
                <ItemRow key={it.id} it={it} onToggle={toggle} onDelete={remove}
                  onEdit={(x) => setForm({ ...x, price: String(x.price), description: x.description || '', image_url: x.image_url || '' })} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}
